import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import {
  servicerInstructionsApi,
  downloadBlob,
  type ServicerInstructionPeriod,
} from "@/api/servicerInstructions";
import { Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useDateFormatter } from "@/utils/datetime";
import { servicerInstructionKeys } from "./servicerInstructionKeys";

/**
 * Payment System v3.0 (WS1) — Servicer Instruction Report detail. Renders the
 * header summary and the servicer-executed disbursements grouped by period.
 */
export function ServicerInstructionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { dateTime } = useDateFormatter();

  const detail = useQuery({
    queryKey: servicerInstructionKeys.detail(id),
    queryFn: () => servicerInstructionsApi.get(id!),
    enabled: !!id,
  });

  if (!id) return null;
  if (detail.isLoading) return <p>Loading…</p>;
  if (detail.isError) return <p>Failed to load report.</p>;
  const report = detail.data;
  if (!report) return null;

  return (
    <>
      <PageHeader
        eyebrow="Servicer Instructions"
        title={report.reportNumber}
        description={`Generated ${dateTime(report.generatedAt)} by ${
          report.generatedByUserId
        } • ${report.totalDisbursements} disbursements totalling $${report.totalAmount.toLocaleString(
          undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        )}`}
        actions={
          <div className="flex gap-space-2">
            <Button
              onClick={async () => {
                const blob = await servicerInstructionsApi.downloadCsv(report.id);
                downloadBlob(blob, `${report.reportNumber}.csv`);
              }}
            >
              <Download size={16} aria-hidden /> CSV
            </Button>
            <Button
              onClick={async () => {
                const blob = await servicerInstructionsApi.downloadPdf(report.id);
                downloadBlob(blob, `${report.reportNumber}.pdf`);
              }}
            >
              <Download size={16} aria-hidden /> PDF
            </Button>
          </div>
        }
      />
      <div className="flex flex-col gap-space-4">
        {report.periods.map((period) => (
          <PeriodSection key={period.period} period={period} />
        ))}
      </div>
    </>
  );
}

function PeriodSection({ period }: { period: ServicerInstructionPeriod }) {
  const [collapsed, setCollapsed] = useState(false);
  const { date } = useDateFormatter();

  return (
    <section className="rounded-md border border-pearl bg-white shadow-sm">
      <header className="flex items-center justify-between px-space-4 py-space-3 border-b border-pearl">
        <button
          type="button"
          className="text-left font-semibold"
          onClick={() => setCollapsed((c) => !c)}
        >
          {period.period}
        </button>
        <span className="text-sm text-graphite-warm">
          {period.lines.length} • $
          {period.totalAmount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </header>
      {!collapsed && (
        <div className="p-space-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-graphite-warm">
                <th className="py-space-1 pr-space-2">Date</th>
                <th className="py-space-1 pr-space-2">Loan</th>
                <th className="py-space-1 pr-space-2">Recipient</th>
                <th className="py-space-1 pr-space-2">Account</th>
                <th className="py-space-1 pr-space-2">Routing</th>
                <th className="py-space-1 pr-space-2 text-right">Amount</th>
                <th className="py-space-1 pr-space-2">Obligation</th>
              </tr>
            </thead>
            <tbody>
              {period.lines.map((line) => {
                const routing = [
                  line.recipientBank,
                  line.recipientAba ? `ABA ${line.recipientAba}` : "",
                  line.recipientAccountNumber,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <tr key={line.disbursementId} className="border-t border-pearl">
                    <td className="py-space-1 pr-space-2 align-top">{date(line.paymentDate)}</td>
                    <td className="py-space-1 pr-space-2 align-top">{line.loan || "—"}</td>
                    <td className="py-space-1 pr-space-2 align-top">{line.recipient || "—"}</td>
                    <td className="py-space-1 pr-space-2 align-top">{line.recipientAccount || "—"}</td>
                    <td className="py-space-1 pr-space-2 align-top text-graphite-warm">
                      {routing || "—"}
                    </td>
                    <td className="py-space-1 pr-space-2 text-right align-top">
                      ${line.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-space-1 pr-space-2 align-top text-graphite-warm">
                      {line.obligation || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
